import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Checkbox,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  styled,
  CircularProgress,
  Chip,
  List,
  ListItemText,
  ListItemButton,
  AppBar,
  Toolbar,
  Slide,
  Grid,
  Avatar,
  Alert,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '../contexts/ThemeContext';
import { scrollableSx } from '../utils/scrollableSx';
import { ScrollIndicatorBox } from './ScrollIndicatorBox';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useAuth } from '../contexts/AuthContext';
import { createEvent, updateEvent, getEvent, inviteUserToEventByEmail, Event } from '../services/eventsService';
import { useSubscription } from '../contexts/SubscriptionContext';
import { createGuest } from '../services/guestsService';
import { collection, query, where, getDocs, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useSafeArea } from '../hooks/useSafeArea';
import { useKeyboardScroll } from '../hooks/useKeyboardScroll';
import { useDialogKeyboardScroll } from '../hooks/useDialogKeyboardScroll';
import { useInventoryContext } from '../contexts/InventoryContext'; // Importar o contexto do inventário
import { notifyEventCreated, notifyEventUpdated, notifyEventShared } from '../services/eventNotificationsService';
import { useLimits } from '../hooks/useLimits';
import { useImageUpload } from '../hooks/useImageUpload';
import { getInventoriesIHaveAccessTo, getSharedInventoryItems } from '../services/inventorySharingService';
import { maxQuantityForSharedItem } from '../utils/sharedInventoryQuantity';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { checkRecipeAllergies, AllergyConflict } from '../services/allergyCheckService';
import { Guest as ServiceGuest } from '../services/guestsService';

interface ExtendedAllergyConflict extends AllergyConflict {
  recipeTitle?: string;
}

// Defina a interface InventoryItem (ajuste os campos conforme necessário)
interface InventoryItem {
  id: string;
  itemName: string;
  photoUrl?: string;
  category?: string;
  description?: string;
  brand?: string;
}



// Interfaces
interface Guest {
  id: string;
  name: string;
  lastName?: string;
  email?: string;
  phone?: string;
  allergies?: string;
}

interface Recipe {
  id: string;
  title: string;
  imageUrl?: string;
  prepTime?: number;
  servings?: number;
  ingredients?: Array<{
    name: string;
    quantity: number;
    unit: string;
  }>;
}

interface Task {
  id: string;
  label: string;
  checked: boolean;
  date?: string;
}

interface EventsDialogProps {
  open: boolean;
  onClose: () => void;
  eventData?: {
    id?: string;
    title: string;
    date: string;
    time: string;
    location?: string;
    description?: string;
    guests?: string[];
    recipes?: string[];
    inventoryItems?: string[];
    inventoryQuantities?: Record<string, number>;
    guestItems?: Record<string, string>;
    tasks?: Task[];
    outfit?: string;
    menu?: string;
    guestCount?: number;
    photos?: string[];
    sharedInventoryItems?: Event['sharedInventoryItems'];
  };
  onEventCreated?: () => void;
}

// Componente de seção reutilizável
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(6), // Espaço para o botão Add
}));

const AddButton = styled(IconButton)(() => ({
  position: 'absolute',
  bottom: 8,
  right: 8,
  zIndex: 1,
}));

// Transição para animação do dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const EventsDialog: React.FC<EventsDialogProps> = ({
  open,
  onClose,
  eventData,
  onEventCreated
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const safeArea = useSafeArea();
  useKeyboardScroll(); // Scroll automático quando teclado aparecer
  const { getAllItems } = useInventoryContext(); // Obter os itens do inventário
  const { featureLimits } = useLimits();
  const { upload, uploading: uploadingPhoto } = useImageUpload();

  // Ref to track if initial data for this dialog session has been loaded
  // This prevents form state from being overwritten when availableGuests/Recipes/Items change references
  const initialDataLoadedRef = React.useRef(false);
  const userTouchedSelectionRef = React.useRef(false);
  const autoSaveDraftIdRef = React.useRef<string | null>(eventData?.id || null);
  const creationNotifiedRef = React.useRef(false);
  const collaboratorsDirtyRef = React.useRef(false);
  const autoSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Reset the ref when the dialog opens/closes or the eventData changes
  React.useEffect(() => {
    if (!open) {
      initialDataLoadedRef.current = false;
      userTouchedSelectionRef.current = false;
    }
  }, [open, eventData?.id]);

  // Estados do formulário
  const [title, setTitle] = useState(eventData?.title || '');
  const [date, setDate] = useState(eventData?.date || '');
  const [time, setTime] = useState(eventData?.time || '19:00');
  const [location, setLocation] = useState(eventData?.location || '');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState<Guest[]>([]);
  const [selectedRecipes, setSelectedRecipes] = useState<Recipe[]>([]);
  const [outfit, setOutfit] = useState('');
  const [menu, setMenu] = useState('');
  const [photos, setPhotos] = useState<string[]>(eventData?.photos || []);
  const [guestCount, setGuestCount] = useState<number>(0);
  const [showAllGuests, setShowAllGuests] = useState(false);
  const [showNewGuestDialog, setShowNewGuestDialog] = useState(false);
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestLastName, setNewGuestLastName] = useState('');
  const [newGuestEmail, setNewGuestEmail] = useState('');
  const [newGuestPhone, setNewGuestPhone] = useState('');
  useDialogKeyboardScroll(showNewGuestDialog);
  const { isPro } = useSubscription();
  const [showCollaboratorDialog, setShowCollaboratorDialog] = useState(false);
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const [pendingCollaborators, setPendingCollaborators] = useState<string[]>([]);
  useDialogKeyboardScroll(showCollaboratorDialog);
  useEffect(() => {
    if (!open) {
      setPendingCollaborators([]);
      setCollaboratorEmail('');
      setShowCollaboratorDialog(false);
      creationNotifiedRef.current = false;
      collaboratorsDirtyRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    let active = true;
    if (open && eventData?.id) {
      getEvent(eventData.id)
        .then((ev) => {
          const persisted = (ev as any)?.pendingCollaboratorEmails;
          if (active && Array.isArray(persisted) && persisted.length > 0) {
            setPendingCollaborators(persisted);
            collaboratorsDirtyRef.current = true;
          }
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [open, eventData?.id]);
  const [savingNewGuest, setSavingNewGuest] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [tasks, setTasks] = useState<Task[]>(
    eventData?.tasks && eventData.tasks.length > 0
      ? eventData.tasks
      : [
        { id: '1', label: '', checked: false },
        { id: '2', label: '', checked: false },
        { id: '3', label: '', checked: false },
      ]
  );
  const [observations, setObservations] = useState(eventData?.description || '');

  // Estados de UI
  const [saving, setSaving] = useState(false);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [showRecipeSelector, setShowRecipeSelector] = useState(false);
  const [allergyConflicts, setAllergyConflicts] = useState<ExtendedAllergyConflict[]>([]);
  const [showAllergyWarning, setShowAllergyWarning] = useState(false);
  const [showItemSelector, setShowItemSelector] = useState(false); // Controle do diálogo de seleção de itens
  const [availableGuests, setAvailableGuests] = useState<Guest[]>([]);
  const [availableRecipes, setAvailableRecipes] = useState<Recipe[]>([]);
  const [availableItems, setAvailableItems] = useState<InventoryItem[]>([]); // Estado para os itens disponíveis
  const [selectedItems, setSelectedItems] = useState<InventoryItem[]>([]); // Estado para os itens selecionados
  const [selectedItemQuantities, setSelectedItemQuantities] = useState<Record<string, number>>({});
  const [guestItems, setGuestItems] = useState<Record<string, string>>({}); // guestId -> what they're bringing
  const [itemPreviewDialog, setItemPreviewDialog] = useState<{
    open: boolean;
    item: InventoryItem | null;
  }>({ open: false, item: null });
  const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, message: '' });

  // Shared inventory states
  const [inventoryTab, setInventoryTab] = useState<'mine' | 'shared'>('mine');
  const [sharedInventories, setSharedInventories] = useState<Array<{ownerId: string, ownerName: string}>>([]);
  const [sharedItems, setSharedItems] = useState<any[]>([]);
  const [loadingSharedItems, setLoadingSharedItems] = useState(false);
  const [selectedSharedItems, setSelectedSharedItems] = useState<Event['sharedInventoryItems']>(eventData?.sharedInventoryItems || []);
  const [pendingSharedItem, setPendingSharedItem] = useState<{item: any, qty: number} | null>(null);

  // Auto-save a cada 30s para não perder dados
  useEffect(() => {
    if (!open || !user?.uid || !hasChanges || !title.trim() || saving) return;

    // Limpa timer anterior
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        const autoSaveData = {
          title: autoSaveDraftIdRef.current && !title.includes('(Rascunho)')
            ? title.trim()
            : title.trim().includes('(Rascunho)') ? title.trim() : title.trim() + ' (Rascunho)',
          description: observations,
          date: date || new Date().toISOString().split('T')[0],
          time: time || '19:00',
          location,
          guestCount: guestCount || 0,
          status: 'planned' as const,
          guests: selectedGuests.map(g => g.id),
          recipes: selectedRecipes.map(r => r.id),
          inventoryItems: selectedItems.map(i => i.id),
          outfit, menu, photos,
          tasks: cleanTasks(),
          pendingCollaboratorEmails: pendingCollaborators,
          ...buildSnapshots(),
        };

        if (autoSaveDraftIdRef.current) {
          // Atualizar evento/rascunho existente
          await updateEvent(autoSaveDraftIdRef.current, autoSaveData);
        } else {
          // Criar rascunho novo (primeira vez)
          const draftId = await createEvent(user.uid, {
            ...autoSaveData,
            title: title.trim() + ' (Rascunho)',
            description: (observations || '') + '\n\n[Rascunho salvo automaticamente]',
          });
          autoSaveDraftIdRef.current = draftId;
        }
        console.log('[AutoSave] Rascunho salvo:', autoSaveDraftIdRef.current);
      } catch (error) {
        console.error('[AutoSave] Erro:', error);
      }
    }, 30000); // 30 segundos

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [open, hasChanges, title, observations, date, time, location, selectedGuests.length, tasks, saving, pendingCollaborators]);

  // Resetar auto-save ref quando dialog fecha
  useEffect(() => {
    if (!open) {
      autoSaveDraftIdRef.current = eventData?.id || null;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    }
  }, [open]);

  // Carregar dados do usuário
  useEffect(() => {
    if (open && user?.uid) {
      loadUserGuests();
      loadUserRecipes();
    }
  }, [open, user?.uid]);

  useEffect(() => {
    if (open) {
      setAvailableItems(getAllItems()); // Carregar os itens do inventário
    }
  }, [open, getAllItems]);

  // Carregar dados existentes do evento quando em modo de edição
  // Only runs once per dialog session to prevent form data from being overwritten
  // when availableGuests/Recipes/Items get new references from context re-renders
  useEffect(() => {
    if (open && eventData && !initialDataLoadedRef.current && !userTouchedSelectionRef.current) {
      // Modo de edição - carregar dados existentes
      setTitle(eventData.title || '');
      setDate(eventData.date || '');
      setTime(eventData.time || '19:00');
      setLocation(eventData.location || '');
      setObservations(eventData.description || '');
      setOutfit(eventData.outfit || '');
      setMenu(eventData.menu || '');
      setPhotos(eventData.photos || []);
      setGuestCount(eventData.guestCount || 0);

      if (eventData.tasks && eventData.tasks.length > 0) {
        setTasks(eventData.tasks);
      } else {
        setTasks([]);
      }

      // Carregar guests selecionados (nao limpa enquanto a lista fonte nao chegou)
      if (availableGuests.length > 0) {
        setSelectedGuests(availableGuests.filter(g => eventData.guests?.includes(g.id)));
      } else if (!eventData.guests || eventData.guests.length === 0) {
        setSelectedGuests([]);
      }

      // Carregar recipes selecionados (nao limpa enquanto a lista fonte nao chegou)
      if (availableRecipes.length > 0) {
        setSelectedRecipes(availableRecipes.filter(r => eventData.recipes?.includes(r.id)));
      } else if (!eventData.recipes || eventData.recipes.length === 0) {
        setSelectedRecipes([]);
      }

      // Carregar inventory items selecionados (nao limpa enquanto a lista fonte nao chegou)
      if (availableItems.length > 0) {
        setSelectedItems(availableItems.filter(i => eventData.inventoryItems?.includes(i.id)));
      } else if (!eventData.inventoryItems || eventData.inventoryItems.length === 0) {
        setSelectedItems([]);
      }

      if (eventData.inventoryQuantities) {
        setSelectedItemQuantities(eventData.inventoryQuantities);
      } else {
        setSelectedItemQuantities({});
      }

      if (eventData.guestItems) {
        setGuestItems(eventData.guestItems);
      } else {
        setGuestItems({});
      }

      // Carregar shared inventory items
      if (eventData.sharedInventoryItems && eventData.sharedInventoryItems.length > 0) {
        setSelectedSharedItems(eventData.sharedInventoryItems);
      } else {
        setSelectedSharedItems([]);
      }

      // Mark as loaded so this effect won't re-run and overwrite user's edits
      // Only mark as loaded if we had the list data needed to resolve guests/recipes/items
      const hasGuestData = !eventData.guests || eventData.guests.length === 0 || availableGuests.length > 0;
      const hasRecipeData = !eventData.recipes || eventData.recipes.length === 0 || availableRecipes.length > 0;
      const hasItemData = !eventData.inventoryItems || eventData.inventoryItems.length === 0 || availableItems.length > 0;
      if (hasGuestData && hasRecipeData && hasItemData) {
        initialDataLoadedRef.current = true;
      }
    } else if (open && !eventData) {
      // Modo de criação - apenas resetar se necessário, mas manter dados do usuário se ele estava editando
      // Não limpar automaticamente para preservar dados em edição
    }
  }, [open, eventData, availableGuests, availableRecipes, availableItems]);

  // Detectar mudanças no formulário
  useEffect(() => {
    if (open) {
      const initialData = {
        title: eventData?.title || '',
        date: eventData?.date || new Date().toISOString().split('T')[0],
        time: eventData?.time || '19:00',
        location: eventData?.location || '',
        observations: eventData?.description || '',
        guests: eventData?.guests || [],
        recipes: eventData?.recipes || [],
        inventoryItems: eventData?.inventoryItems || [],
        tasks: eventData?.tasks || [],
        photos: eventData?.photos || []
      };

      const currentData = {
        title,
        date,
        time,
        location,
        observations,
        guests: selectedGuests.map(g => g.id),
        recipes: selectedRecipes.map(r => r.id),
        inventoryItems: selectedItems.map(i => i.id),
        tasks: cleanTasks(),
        photos
      };

      const hasChanged = JSON.stringify(initialData) !== JSON.stringify(currentData);
      setHasChanges(hasChanged);
    }
  }, [title, date, time, location, observations, selectedGuests, selectedRecipes, selectedItems, tasks, outfit, photos, open, eventData]);

  // Verificar conflitos de alergia sempre que convidados ou receitas mudarem
  useEffect(() => {
    const checkConflicts = async () => {
      if (selectedGuests.length > 0 && selectedRecipes.length > 0 && user?.uid) {
        // Mapear objetos locais para formatos compatíveis se necessário
        // Como o checkRecipeAllergies aceita Objetos de Recipe e Lista de Guests, podemos passar direto

        // Porém, a função espera UMA receita + LISTA de guests para análise detalhada
        // Vamos iterar sobre as receitas selecionadas

        let allConflicts: ExtendedAllergyConflict[] = [];

        // Mapear guests locais para o tipo esperado pelo service
        // O service espera Guest com campos completos de DB, mas para verificação de alergia in-memory
        // só precisamos de id, name, e allergies. O resto pode ser mockado/opcional.
        // Vamos fazer um cast seguro ou mapeamento.
        const serviceGuests: ServiceGuest[] = selectedGuests.map(g => ({
          id: g.id,
          userId: user.uid,
          name: g.name,
          lastName: g.lastName,
          email: g.email || '',
          phone: g.phone || '',
          allergies: g.allergies || '',
          status: 'confirmed', // Mock
          createdAt: new Date(),
          updatedAt: new Date()
        }));

        for (const recipe of selectedRecipes) {
          // Converter Recipe local para formato esperado pelo service se necessário
          // O service espera ingredients com array de objetos, o nosso local já tem essa estrutura
          // Mas garantindo compatibilidade de tipos:
          const serviceRecipe = {
            ...recipe,
            userId: user.uid, // Mock, não usado para check in-memory
            id: recipe.id,
            title: recipe.title,
            ingredients: recipe.ingredients || [],
            instructions: '',
            servings: recipe.servings || 1,
            prepTime: recipe.prepTime || 0,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          const conflicts = await checkRecipeAllergies(
            user.uid,
            serviceRecipe,
            serviceGuests // Passar lista mapeada
          );

          if (conflicts.length > 0) {
            // O service retorna conflitos por Guest -> Ingredientes
            // Vamos adaptar para mostrar no dialog de aviso global se necessário, ou acumular
            // O AllergyWarningDialog espera AllergyConflict[] do service, então estamos ok
            // Mas aqui estamos iterando receitas, então precisamos anotar qual receita deu o problema

            // O retorno do service é AllergyConflict[] que tem guestId, guestName, allergens, conflictingIngredients
            // Não tem o nome da receita, então vamos adicionar se a interface pedir

            // O AllergyWarningDialog atual itera conflitos. 
            // Se tivermos conflitos de várias receitas, o ideal seria mostrar alerta para cada uma ou agrupar.
            // O Dialog atual de Warning mosta UMA receita ("A receita X contém...").

            // Simplificação: Se houver qualquer conflito, pegamos o primeiro lote de conflitos e mostramos
            // Ou melhor, acumulamos tudo.

            // A interface AllergyConflict do service NÃO tem o nome da receita. 
            // Vamos precisar adaptar o AllergyWarningDialog para suportar múltiplas receitas ou
            // Fazer verificações individuais.

            // O requisito original era triangulação ao ADICIONAR a receita.
            // Aqui estamos num useEffect que roda sempre que selecionados mudam.

            allConflicts = [...allConflicts, ...conflicts.map(c => ({
              ...c,
              // Adicionando propriedade recipeTitle dinamicamente se precisarmos exibir
              recipeTitle: recipe.title
            }))];
          }
        }

        // Remover duplicatas se houver (mesmo guest, mesmos ingredientes)

        if (allConflicts.length > 0) {
          setAllergyConflicts(allConflicts);
          // Só abrimos o warning explicitamente ao adicionar a receita (handle logic abaixo), 
          // ou mostramos um warning passivo na UI?
          // O código original setava setShowAllergyWarning(true) no array de dependências.
          // Isso pode ser irritante se abrir popup toda hora.
          // Vamos apenas atualizar o estado de conflitos, e mostrar um alerta visual na UI (Badge ou Texto), 
          // e talvez o popup só quando adiciona NOVO item.

          // Mas para manter compatibilidade com o comportamento anterior:
          // O anterior abria o warning. Vamos manter mas com cuidado.
          // Na verdade, o useEffect anterior chamava checkAllergyConflicts().

          setAllergyConflicts(allConflicts);
        } else {
          setAllergyConflicts([]);
        }
      } else {
        setAllergyConflicts([]);
      }
    };

    checkConflicts();
  }, [selectedGuests, selectedRecipes, user?.uid]);

  const loadUserGuests = async () => {
    if (!user?.uid) return;

    try {
      setLoadingGuests(true);
      const guestsRef = collection(db, 'guests');
      const q = query(guestsRef, where('userId', '==', user.uid));
      const snapshot = await getDocs(q);

      const guests: Guest[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        guests.push({
          id: doc.id,
          name: data.name || '',
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          allergies: data.allergies,
        });
      });

      setAvailableGuests(guests);
    } catch (error) {
      console.error('Erro ao carregar convidados:', error);
    } finally {
      setLoadingGuests(false);
    }
  };

  const loadUserRecipes = async () => {
    if (!user?.uid) return;

    try {
      setLoadingRecipes(true);
      const recipesRef = collection(db, 'recipes');
      const q = query(recipesRef, where('userId', '==', user.uid));
      const snapshot = await getDocs(q);

      const recipes: Recipe[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        recipes.push({
          id: doc.id,
          title: data.title || '',
          imageUrl: data.imageUrl, // Incluir imageUrl
          ingredients: data.ingredients || [],
        });
      });

      setAvailableRecipes(recipes);
    } catch (error) {
      console.error('Erro ao carregar receitas:', error);
    } finally {
      setLoadingRecipes(false);
    }
  };

  const cleanTasks = () => tasks
    .filter(t => t.label.trim() !== '')
    .map(t => ({ id: t.id, label: t.label, checked: t.checked, ...(t.date ? { date: t.date } : {}) }));

  const handleUpdateTaskDate = (id: string, date: string) => {
    setTasks(tasks.map(task => task.id === id ? { ...task, date } : task));
  };

  const handleAddTask = () => {
    setTasks([...tasks, { id: Date.now().toString(), label: '', checked: false }]);
  };

  const handleToggleTask = (id: string) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, checked: !task.checked } : task
    ));
  };

  const handleUpdateTaskLabel = (id: string, label: string) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, label } : task
    ));
  };

  const handleDeleteTask = (id: string) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const handleAddGuest = (guest: Guest) => {
    userTouchedSelectionRef.current = true;
    const isAlreadySelected = selectedGuests.find(g => g.id === guest.id);
    if (isAlreadySelected) {
      setSelectedGuests(selectedGuests.filter(g => g.id !== guest.id));
    } else {
      setSelectedGuests([...selectedGuests, guest]);
    }
  };

  const handleRemoveGuest = (guestId: string) => {
    setSelectedGuests(selectedGuests.filter(g => g.id !== guestId));
    setGuestItems(prev => {
      const updated = { ...prev };
      delete updated[guestId];
      return updated;
    });
  };

  const handleCreateNewGuest = async () => {
    if (!user?.uid || !newGuestName.trim()) return;

    try {
      setSavingNewGuest(true);
      const guestId = await createGuest(user.uid, {
        name: newGuestName,
        lastName: newGuestLastName || undefined,
        email: newGuestEmail || undefined,
        phone: newGuestPhone || undefined,
        status: 'pending',
      });

      const newGuest: Guest = {
        id: guestId,
        name: newGuestName,
        lastName: newGuestLastName || undefined,
        email: newGuestEmail || undefined,
        phone: newGuestPhone || undefined,
      };

      setAvailableGuests([...availableGuests, newGuest]);
      setSelectedGuests([...selectedGuests, newGuest]);

      setNewGuestName('');
      setNewGuestLastName('');
      setNewGuestEmail('');
      setNewGuestPhone('');
      setShowNewGuestDialog(false);
      setShowGuestSelector(true);
    } catch (error) {
      console.error('Erro ao criar convidado:', error);
    } finally {
      setSavingNewGuest(false);
    }
  };

  const [imagePreview, setImagePreview] = useState<{ src: string; name: string } | null>(null);
  const longPressRef = React.useRef<NodeJS.Timeout | null>(null);
  const longPressFiredRef = React.useRef(false);

  const startLongPress = (src?: string, name?: string) => {
    if (!src) return;
    cancelLongPress();
    longPressRef.current = setTimeout(async () => {
      longPressFiredRef.current = true;
      try { await Haptics.impact({ style: ImpactStyle.Medium }); } catch { void 0; }
      setImagePreview({ src, name: name || '' });
    }, 450);
  };

  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  const buildSnapshots = () => ({
    inventorySnapshot: selectedItems.map(i => ({
      id: i.id,
      name: i.itemName || '',
      photoUrl: i.photoUrl || '',
      quantity: selectedItemQuantities[i.id] || 1,
    })),
    recipesSnapshot: selectedRecipes.map(r => ({
      id: r.id,
      title: r.title || '',
      imageUrl: r.imageUrl || '',
    })),
  });

  const handleAddRecipe = (recipe: Recipe) => {
    userTouchedSelectionRef.current = true;
    if (selectedRecipes.find(r => r.id === recipe.id)) {
      setSelectedRecipes(selectedRecipes.filter(r => r.id !== recipe.id));
    } else {
      setSelectedRecipes([...selectedRecipes, recipe]);
    }

    // Verificação imediata ao adicionar
    // (Opcional, pois o useEffect vai rodar, mas para feedback imediato antes de fechar selector se fosse o caso)
    // Como o selector não fecha, o useEffect vai cuidar de atualizar os conflitos.
  }
  // NÃO FECHAR O DIALOG - remover esta linha:
  // setShowRecipeSelector(false);


  const handleRemoveRecipe = (recipeId: string) => {
    userTouchedSelectionRef.current = true;
    setSelectedRecipes(selectedRecipes.filter(r => r.id !== recipeId));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    const photosLimit = featureLimits?.photosPerEvent || 0;
    if (photosLimit !== -1 && photos.length >= photosLimit) {
      setAlertDialog({ open: true, message: `O limite do seu plano é de ${photosLimit} fotos por evento.` });
      return;
    }

    const url = await upload(file, `events/${user?.uid}/event-photos`);
    if (url) {
      setPhotos(prev => [...prev, url]);
    }

    e.target.value = '';
  };

  const handleRemovePhoto = (url: string) => {
    setPhotos(photos.filter(p => p !== url));
  };



  const handleAddItem = (item: InventoryItem) => {
    userTouchedSelectionRef.current = true;
    if (selectedItems.find((i) => i.id === item.id)) {
      setSelectedItems(selectedItems.filter((i) => i.id !== item.id));
      setSelectedItemQuantities(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }
    setSelectedItems([...selectedItems, item]);
    setSelectedItemQuantities(prev => ({
      ...prev,
      [item.id]: 1
    }));
  };

  const handleRemoveItem = (itemId: string) => {
    userTouchedSelectionRef.current = true;
    setSelectedItems(selectedItems.filter((i) => i.id !== itemId));
    const newQuantities = { ...selectedItemQuantities };
    delete newQuantities[itemId];
    setSelectedItemQuantities(newQuantities);
  };

  const handleUpdateItemQuantity = (itemId: string, delta: number) => {
    const stock = (selectedItems.find(i => i.id === itemId) as any)?.quantity;
    setSelectedItemQuantities(prev => {
      const currentQty = prev[itemId] || 1;
      let newQty = Math.max(1, currentQty + delta);
      if (typeof stock === 'number' && stock > 0) newQty = Math.min(newQty, stock);
      return {
        ...prev,
        [itemId]: newQty
      };
    });
  };

  const handleItemPressStart = (item: InventoryItem) => {
    const timer = setTimeout(() => {
      setItemPreviewDialog({ open: true, item });
    }, 500); // 500ms para considerar como "pressionar e segurar"
    setPressTimer(timer);
  };

  const handleItemPressEnd = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  // Shared inventory handlers
  const loadSharedInventories = async () => {
    if (!user?.uid) return;
    setLoadingSharedItems(true);
    try {
      const accesses = await getInventoriesIHaveAccessTo(user.uid);
      const inventories = accesses.map(a => ({ ownerId: a.ownerId, ownerName: a.ownerName }));
      setSharedInventories(inventories);

      // Load items from all shared inventories
      const allItems: any[] = [];
      for (const inv of inventories) {
        try {
          const items = await getSharedInventoryItems(inv.ownerId, user.uid);
          const itemsWithOwner = items.map(item => ({
            ...item,
            _ownerId: inv.ownerId,
            _ownerName: inv.ownerName,
          }));
          allItems.push(...itemsWithOwner);
        } catch (err) {
          console.error(`Erro ao carregar itens de ${inv.ownerName}:`, err);
        }
      }
      setSharedItems(allItems);
    } catch (error) {
      console.error('Erro ao carregar inventários compartilhados:', error);
    } finally {
      setLoadingSharedItems(false);
    }
  };

  const handleAddSharedItem = (item: any, quantity: number = 1) => {
    const alreadyAdded = selectedSharedItems?.find(
      si => si.itemId === item.id && si.ownerId === item._ownerId
    );
    if (!alreadyAdded) {
      setSelectedSharedItems(prev => [
        ...(prev || []),
        {
          itemId: item.id,
          itemName: item.itemName,
          ownerId: item._ownerId,
          ownerName: item._ownerName,
          quantity,
        },
      ]);
    }
    setPendingSharedItem(null);
  };

  const handleRemoveSharedItem = (itemId: string, ownerId: string) => {
    setSelectedSharedItems(prev =>
      (prev || []).filter(si => !(si.itemId === itemId && si.ownerId === ownerId))
    );
  };

  const handleCloseDialog = () => {
    if (hasChanges && title.trim()) {
      setShowExitDialog(true);
    } else {
      onClose();
    }
  };

  const handleSaveDraft = async () => {
    if (!user?.uid || !title.trim()) return;

    // Editando um evento JÁ existente e publicado: salva de verdade pelo caminho
    // vetado (updateEvent + notificações), sem criar cópia "(Rascunho)".
    // Se ainda é rascunho, mantém rascunho e NÃO notifica convidados — publicar
    // é uma ação explícita do usuário.
    if (eventData?.id && !isEditingDraft) {
      setShowExitDialog(false);
      await handleSaveEvent();
      return;
    }

    try {
      setSaving(true);
      // Filter guestItems for draft
      const filteredGuestItemsDraft: Record<string, string> = {};
      selectedGuests.forEach(g => {
        if (guestItems[g.id]?.trim()) {
          filteredGuestItemsDraft[g.id] = guestItems[g.id].trim();
        }
      });

      const baseTitle = title.trim().replace(' (Rascunho)', '');
      const baseDescription = (observations || '').replace('\n\n[Rascunho salvo automaticamente]', '');

      const draftData: any = {
        title: baseTitle + ' (Rascunho)',
        description: baseDescription + '\n\n[Rascunho salvo automaticamente]',
        date: date || new Date().toISOString().split('T')[0],
        time: time || '19:00',
        location: location || '',
        guestCount: selectedGuests.length,
        status: 'planned' as const,
        guests: selectedGuests.map(g => g.id),
        recipes: selectedRecipes.map(r => r.id),
        inventoryItems: selectedItems.map(i => i.id),
        guestItems: filteredGuestItemsDraft,
        outfit: outfit || '',
        menu: menu || '',
        photos: photos || [],
        tasks: cleanTasks(),
        pendingCollaboratorEmails: pendingCollaborators,
        ...buildSnapshots(),
      };
      if (selectedSharedItems && selectedSharedItems.length > 0) {
        draftData.sharedInventoryItems = selectedSharedItems;
      }

      // Se o auto-save já criou um rascunho, ATUALIZA em vez de criar um segundo.
      if (autoSaveDraftIdRef.current) {
        await updateEvent(autoSaveDraftIdRef.current, draftData);
      } else {
        const draftId = await createEvent(user.uid, draftData);
        autoSaveDraftIdRef.current = draftId;
      }
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
    } finally {
      setSaving(false);
      setShowExitDialog(false);
    }
  };

  // Sync event tasks to the todos Firestore collection
  const syncEventTasksToTodos = async (
    eventId: string,
    eventTitle: string,
    eventDate: string,
    eventTasks: Task[],
    userId: string
  ) => {
    if (!eventTasks || eventTasks.length === 0) return;

    const dateForTodo = new Date(eventDate + 'T00:00:00');

    for (let i = 0; i < eventTasks.length; i++) {
      const task = eventTasks[i];
      if (!task.label.trim()) continue;

      // Deterministic ID based on eventId + task index to avoid duplicates
      const todoId = `event_${eventId}_task_${i}`;
      const todoRef = doc(db, 'todos', todoId);

      await setDoc(todoRef, {
        title: task.label,
        completed: task.checked,
        date: Timestamp.fromDate(dateForTodo),
        userId,
        eventId,
        eventTitle,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }, { merge: true });
    }
  };

  const applyPendingCollaborators = async (finalEventId: string): Promise<string[]> => {
    const emails = pendingCollaborators;
    const failed: string[] = [];
    for (const email of emails) {
      try {
        const ev = await getEvent(finalEventId);
        if (!ev) {
          failed.push(email);
          continue;
        }
        const result = await inviteUserToEventByEmail(ev, email, true);
        if (!result.ok) {
          failed.push(email);
          continue;
        }
        const notified = await notifyEventShared(ev, finalEventId, result.user.uid, true);
        if (!notified) {
          failed.push(email);
        }
      } catch (e) {
        console.error('Erro ao convidar colaborador:', e);
        failed.push(email);
      }
    }
    setPendingCollaborators(failed);
    if (collaboratorsDirtyRef.current || emails.length > 0) {
      await updateEvent(finalEventId, { pendingCollaboratorEmails: failed });
      collaboratorsDirtyRef.current = failed.length > 0;
    }
    return failed;
  };

  const handleOpenCollaborators = () => {
    if (!isPro) {
      setAlertDialog({ open: true, message: 'Convidar alguém para co-editar o evento é um recurso do plano Pro.' });
      return;
    }
    setShowCollaboratorDialog(true);
  };

  const handleAddCollaborator = () => {
    const email = collaboratorEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setPendingCollaborators((prev) => (prev.includes(email) ? prev : [...prev, email]));
    setCollaboratorEmail('');
    setHasChanges(true);
    collaboratorsDirtyRef.current = true;
  };

  const handleSaveEvent = async () => {
    // Validação mínima: apenas título é obrigatório
    if (!user?.uid) return;

    if (!title.trim()) {
      setAlertDialog({ open: true, message: 'Digite o nome do evento' });
      return;
    }

    // Se não houver data, usar a data atual
    const eventDate = date || new Date().toISOString().split('T')[0];

    try {
      setSaving(true);

      // Filter guestItems to only include selected guests, and remove empty values
      const filteredGuestItems: Record<string, string> = {};
      selectedGuests.forEach(g => {
        if (guestItems[g.id]?.trim()) {
          filteredGuestItems[g.id] = guestItems[g.id].trim();
        }
      });

      const eventDataToSave: any = {
        title: title.trim(),
        description: observations || '',
        date: eventDate,
        time: time || '19:00',
        location: location || '',
        guestCount: guestCount || 0,
        status: 'planned' as const,
        guests: selectedGuests.map(g => g.id),
        recipes: selectedRecipes.map(r => r.id),
        inventoryItems: selectedItems.map(i => i.id),
        inventoryQuantities: selectedItemQuantities,
        guestItems: Object.keys(filteredGuestItems).length > 0 ? filteredGuestItems : {},
        outfit: outfit || '',
        menu: menu || '',
        photos: photos || [],
        tasks: cleanTasks(),
        ...buildSnapshots(),
      };

      // Firestore não aceita undefined - só adicionar se tiver items
      if (selectedSharedItems && selectedSharedItems.length > 0) {
        eventDataToSave.sharedInventoryItems = selectedSharedItems;
      }

      // Verificar se é edição, auto-save existente, ou criação nova
      const existingId = eventData?.id || autoSaveDraftIdRef.current;
      let collabFailed: string[] = [];

      if (existingId) {
        // Modo edição ou auto-save existente: atualizar evento
        const oldEvent = await getEvent(existingId);

        // Remove marcas de rascunho se houver
        if (eventDataToSave.title.includes('(Rascunho)')) {
          eventDataToSave.title = eventDataToSave.title.replace(' (Rascunho)', '');
        }
        if (eventDataToSave.description?.includes('[Rascunho salvo automaticamente]')) {
          eventDataToSave.description = eventDataToSave.description.replace('\n\n[Rascunho salvo automaticamente]', '');
        }

        await updateEvent(existingId, eventDataToSave);

        // Enviar notificações de atualização
        if (oldEvent && eventData?.id) {
          notifyEventUpdated(existingId, oldEvent, eventDataToSave).catch(error => {
            console.error('Erro ao enviar notificações de atualização:', error);
          });
        } else if (!eventData?.id && !creationNotifiedRef.current) {
          creationNotifiedRef.current = true;
          notifyEventCreated({ ...eventDataToSave, userId: user.uid, id: existingId } as any, existingId).catch(error => {
            console.error('Erro ao enviar notificações de criação:', error);
          });
        }

        // Sync event tasks to todos collection
        syncEventTasksToTodos(existingId, title.trim(), eventDate, eventDataToSave.tasks, user.uid).catch(error => {
          console.error('Erro ao sincronizar tarefas do evento:', error);
        });

        collabFailed = await applyPendingCollaborators(existingId);
      } else {
        // Modo criação: criar novo evento
        const eventId = await createEvent(user.uid, eventDataToSave);
        autoSaveDraftIdRef.current = eventId;

        if (!creationNotifiedRef.current) {
          creationNotifiedRef.current = true;
          notifyEventCreated({ ...eventDataToSave, userId: user.uid, id: eventId } as any, eventId).catch(error => {
            console.error('Erro ao enviar notificações de criação:', error);
          });
        }

        // Sync event tasks to todos collection
        syncEventTasksToTodos(eventId, title.trim(), eventDate, eventDataToSave.tasks, user.uid).catch(error => {
          console.error('Erro ao sincronizar tarefas do evento:', error);
        });

        collabFailed = await applyPendingCollaborators(eventId);
      }

      if (collabFailed.length > 0) {
        setAlertDialog({ open: true, message: `Evento salvo, mas não foi possível convidar: ${collabFailed.join(', ')}. Confira se usam o Hostly e tente de novo pelo botão de colaborar.` });
      } else {
        if (onEventCreated) {
          onEventCreated();
        }
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      setAlertDialog({ open: true, message: 'Erro ao salvar o evento. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const isEditingDraft = !!(eventData?.id && eventData?.title?.includes('(Rascunho)'));

  const handlePublishEvent = async () => {
    if (!user?.uid) return;

    if (!title.trim()) {
      setAlertDialog({ open: true, message: 'Digite o nome do evento' });
      return;
    }

    const eventDate = date || new Date().toISOString().split('T')[0];

    try {
      setSaving(true);

      // Strip draft markers from title and description
      const cleanTitle = title.trim().replace(' (Rascunho)', '').replace(' (Rascunho)', '');
      const cleanDescription = observations
        .replace('\n\n[Rascunho salvo automaticamente]', '')
        .replace('[Rascunho salvo automaticamente]', '');

      // Filter guestItems for publish
      const filteredGuestItemsPublish: Record<string, string> = {};
      selectedGuests.forEach(g => {
        if (guestItems[g.id]?.trim()) {
          filteredGuestItemsPublish[g.id] = guestItems[g.id].trim();
        }
      });

      const eventDataToSave: any = {
        title: cleanTitle,
        description: cleanDescription || '',
        date: eventDate,
        time: time || '19:00',
        location: location || '',
        guestCount: guestCount || 0,
        status: 'planned' as const,
        guests: selectedGuests.map(g => g.id),
        recipes: selectedRecipes.map(r => r.id),
        inventoryItems: selectedItems.map(i => i.id),
        inventoryQuantities: selectedItemQuantities,
        guestItems: filteredGuestItemsPublish,
        outfit: outfit || '',
        menu: menu || '',
        photos: photos || [],
        tasks: cleanTasks(),
        ...buildSnapshots(),
      };
      if (selectedSharedItems && selectedSharedItems.length > 0) {
        eventDataToSave.sharedInventoryItems = selectedSharedItems;
      }

      let collabFailed: string[] = [];
      if (eventData?.id) {
        await updateEvent(eventData.id, eventDataToSave);

        if (!creationNotifiedRef.current) {
          creationNotifiedRef.current = true;
          notifyEventCreated({ ...eventDataToSave, userId: user.uid, id: eventData.id } as any, eventData.id).catch(error => {
            console.error('Erro ao enviar notificações de publicação:', error);
          });
        }

        collabFailed = await applyPendingCollaborators(eventData.id);
      }

      if (collabFailed.length > 0) {
        setAlertDialog({ open: true, message: `Evento salvo, mas não foi possível convidar: ${collabFailed.join(', ')}. Confira se usam o Hostly e tente de novo pelo botão de colaborar.` });
      } else {
        if (onEventCreated) {
          onEventCreated();
        }
        onClose();
      }
    } catch (error) {
      console.error('Erro ao publicar evento:', error);
    } finally {
      setSaving(false);
    }
  };

  // Funcionalidades futuras (preparação)
  const handleSendWhatsApp = () => {
    setShowWhatsAppDialog(true);
  };

  const handleSendEmail = () => {
    setShowEmailDialog(true);
  };

  const handleSendToGuest = (guest: Guest, method: 'whatsapp' | 'email') => {
    if (!eventData?.id || !title || !date || !time) return;

    const willBring = guestItems[guest.id];
    const eventDetails = `Convite: ${title}\n\n` +
      `Data: ${new Date(date).toLocaleDateString('pt-BR')}\n` +
      `Horário: ${time}\n` +
      (location ? `Local: ${location}\n` : '') +
      (outfit ? `O que usar: ${outfit}\n` : '') +
      (willBring ? `\nVocê vai levar: ${willBring}\n` : '') +
      (observations ? `\n${observations}\n\n` : '\n\n') +
      `Enviado pelo Hostly App\n` +
      `Ainda não tem o Hostly? Baixe em https://hostly.app.br`;

    if (method === 'whatsapp' && guest.phone) {
      const cleanPhone = guest.phone.replace(/\D/g, '');
      const phone = cleanPhone.length <= 11 ? `55${cleanPhone}` : cleanPhone;
      const message = encodeURIComponent(eventDetails);
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    } else if (method === 'email' && guest.email) {
      const subject = encodeURIComponent(`Convite: ${title}`);
      const body = encodeURIComponent(eventDetails);
      window.location.href = `mailto:${guest.email}?subject=${subject}&body=${body}`;
    }
  };

  const handleAddToGoogleCalendar = () => {
    if (!title || !date || !time) return;

    const startDateTime = new Date(`${date}T${time}`);
    const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000); // +2 horas

    const formatDateForCalendar = (date: Date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    };

    const calendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(title)}` +
      `&dates=${formatDateForCalendar(startDateTime)}/${formatDateForCalendar(endDateTime)}` +
      (location ? `&location=${encodeURIComponent(location)}` : '') +
      (observations ? `&details=${encodeURIComponent(observations)}` : '');

    window.open(calendarUrl, '_blank');
  };

  // Resetar campos quando o dialog abrir/fechar
  useEffect(() => {
    if (open && !eventData) {
      // Apenas resetar se for criação (não edição)
      setTitle('');
      setDate(new Date().toISOString().split('T')[0]);
      setTime('19:00');
      setLocation('');
      setSelectedGuests([]);
      setSelectedRecipes([]);
      setSelectedGuests([]);
      setSelectedRecipes([]);
      setSelectedItems([]);
      setSelectedItemQuantities({});
      setOutfit('');
      setMenu('');
      setPhotos([]);
      setObservations('');
      setTasks([
        { id: '1', label: '', checked: false },
        { id: '2', label: '', checked: false },
        { id: '3', label: '', checked: false },
      ]);
    }
  }, [open, eventData]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      slots={{
        transition: Transition
      }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: theme.colors.background,
            display: 'flex',
            flexDirection: 'column',
          }
        }
      }}
    >
      {/* Container Principal Flex */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* Header - Static no Flex Container */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: theme.colors.background,
            borderBottom: `1px solid ${theme.colors.border}`,
            paddingTop: `${safeArea.top}px`,
          }}
        >
          <Toolbar>
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nome do Evento *"
              variant="standard"
              sx={{
                flex: 1,
                '& .MuiInput-root': {
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: theme.colors.text.primary,
                  '&:before': {
                    borderBottom: 'none',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none',
                  },
                  '&.Mui-focused:after': {
                    borderBottom: `2px solid ${theme.colors.primary}`,
                  },
                },
                '& .MuiInput-input::placeholder': {
                  color: theme.colors.text.secondary,
                  opacity: 0.7,
                },
              }}
            />
            <IconButton
              edge="end"
              onClick={handleCloseDialog}
              sx={{
                color: theme.colors.text.primary,
                ml: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Conteúdo com scroll e Flex Grow */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            paddingLeft: `${safeArea.left}px`,
            paddingRight: `${safeArea.right}px`,
          }}
        >
          <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            <Grid container spacing={2}>
              {/* Data e Hora */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ backgroundColor: theme.colors.card }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: theme.colors.text.primary }}>
                      Data:
                    </Typography>
                    <Button
                      onClick={() => setShowDatePicker(true)}
                      size="small"
                      fullWidth
                      sx={{
                        flex: 1,
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.text.contrast,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        px: 2,
                        py: 1,
                        '&:hover': {
                          backgroundColor: theme.colors.primary,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {date ? new Date(date + 'T00:00:00').toLocaleDateString('pt-BR') : 'Selecionar data'}
                    </Button>
                  </Box>
                </SectionPaper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ backgroundColor: theme.colors.card }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: theme.colors.text.primary }}>
                      Hora:
                    </Typography>
                    <TextField
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      size="small"
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.colors.primary,
                          color: theme.colors.text.contrast,
                          '& fieldset': { borderColor: 'transparent' },
                        },
                      }}
                    />
                  </Box>
                </SectionPaper>
              </Grid>

              {/* Convidados */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" sx={{ color: theme.colors.text.primary }}>Convidados:</Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      label="Quantidade de pessoas"
                      value={guestCount || ''}
                      onChange={(e) => setGuestCount(Number(e.target.value))}
                      slotProps={{
                        htmlInput: { min: 0 }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          backgroundColor: theme.colors.background,
                          px: 0.5,
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: showAllGuests ? 'none' : '200px', overflow: 'hidden' }}>
                      {selectedGuests.map((guest) => (
                        <Box key={guest.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={guest.lastName ? `${guest.name} ${guest.lastName}` : guest.name}
                              onDelete={() => handleRemoveGuest(guest.id)}
                              deleteIcon={<CloseIcon />}
                              size="small"
                              sx={{
                                backgroundColor: guest.allergies ? '#ff9800' : theme.colors.primary,
                                color: theme.colors.text.contrast,
                                '& .MuiChip-deleteIcon': {
                                  color: theme.colors.text.contrast,
                                  fontSize: '18px',
                                  '&:hover': {
                                    color: theme.colors.text.contrast,
                                    opacity: 0.7,
                                  }
                                }
                              }}
                            />
                          </Box>
                          <TextField
                            size="small"
                            placeholder="Vai levar..."
                            value={guestItems[guest.id] || ''}
                            onChange={(e) => setGuestItems(prev => ({
                              ...prev,
                              [guest.id]: e.target.value
                            }))}
                            variant="outlined"
                            sx={{
                              ml: 1,
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.8rem',
                                height: '32px',
                                '& fieldset': {
                                  borderColor: theme.colors.border,
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '4px 8px',
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                    {selectedGuests.some(g => g.allergies) && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          Atenção - Restrições Alimentares:
                        </Typography>
                        {selectedGuests.filter(g => g.allergies).map((guest) => (
                          <Typography key={guest.id} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            • <strong>{guest.lastName ? `${guest.name} ${guest.lastName}` : guest.name}</strong>: {guest.allergies}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                    {selectedGuests.length > 5 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllGuests(!showAllGuests)}
                        sx={{
                          color: theme.colors.primary,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                        }}
                      >
                        {showAllGuests ? 'Ver menos' : `Ver todos (${selectedGuests.length})`}
                      </Button>
                    )}
                  </Box>
                  <AddButton onClick={() => setShowGuestSelector(true)}>
                    <AddCircleOutlineIcon sx={{ color: theme.colors.primary }} />
                  </AddButton>
                </SectionPaper>
              </Grid>

              {/* Onde será */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" sx={{ color: theme.colors.text.primary }}>Onde será:</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Endereço do evento (opcional)"
                    variant="standard"
                    sx={{ mt: 1, color: theme.colors.text.primary }}
                  />
                </SectionPaper>
              </Grid>

              {/* O que usar */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" sx={{ color: theme.colors.text.primary }}>O que usar:</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={outfit}
                    onChange={(e) => setOutfit(e.target.value)}
                    placeholder="Dress code / Roupa"
                    variant="standard"
                    sx={{ mt: 1, color: theme.colors.text.primary }}
                  />
                </SectionPaper>
              </Grid>

              {/* Menu - para comidas prontas ou cada um leva algo */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" sx={{ color: theme.colors.text.primary }}>Menu:</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={menu}
                    onChange={(e) => setMenu(e.target.value)}
                    placeholder="Comidas prontas, cada um leva algo..."
                    variant="standard"
                    sx={{ mt: 1, color: theme.colors.text.primary }}
                  />
                </SectionPaper>
              </Grid>

              {/* Receitas */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Typography variant="h6" sx={{ color: theme.colors.text.primary, mb: 1 }}>Receitas:</Typography>
                    <ScrollIndicatorBox theme={theme} maxHeight={80} sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
                      {selectedRecipes.map((recipe) => (
                        <Box
                          key={recipe.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: theme.colors.primary,
                            borderRadius: theme.radii.small,
                            padding: '6px 8px',
                          }}
                        >
                          {recipe.imageUrl ? (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: theme.radii.small,
                                backgroundImage: `url(${recipe.imageUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Avatar
                              variant="rounded"
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: theme.colors.secondary,
                                fontSize: '0.75rem',
                                flexShrink: 0,
                              }}
                            >
                              🍽️
                            </Avatar>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.colors.text.contrast,
                              flex: 1,
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {recipe.title}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRecipe(recipe.id)}
                            sx={{
                              color: theme.colors.text.contrast,
                              padding: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.2)',
                              flexShrink: 0,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                              }
                            }}
                          >
                            <CloseIcon sx={{ fontSize: '0.875rem' }} />
                          </IconButton>
                        </Box>
                      ))}
                      {selectedRecipes.length === 0 && (
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', fontStyle: 'italic' }}>
                          Nenhuma receita adicionada
                        </Typography>
                      )}
                    </ScrollIndicatorBox>
                  </Box>
                  <AddButton onClick={() => setShowRecipeSelector(true)}>
                    <AddCircleOutlineIcon sx={{ color: theme.colors.primary }} />
                  </AddButton>
                </SectionPaper>
              </Grid>

              {/* Itens do Acervo */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Typography variant="h6" sx={{ color: theme.colors.text.primary, mb: 1 }}>Itens do Acervo:</Typography>
                    <ScrollIndicatorBox theme={theme} maxHeight={80} sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
                      {selectedItems.map((item) => (
                        <Box
                          key={item.id}
                          onMouseDown={() => handleItemPressStart(item)}
                          onMouseUp={handleItemPressEnd}
                          onMouseLeave={handleItemPressEnd}
                          onTouchStart={() => handleItemPressStart(item)}
                          onTouchEnd={handleItemPressEnd}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: theme.colors.primary,
                            borderRadius: theme.radii.small,
                            padding: '6px 8px',
                            cursor: 'pointer',
                            transition: 'transform 0.1s',
                            '&:active': {
                              transform: 'scale(0.98)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: theme.radii.small,
                              backgroundImage: item.photoUrl ? `url(${item.photoUrl})` : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              flexShrink: 0,
                            }}
                          >
                            {!item.photoUrl && '📦'}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.colors.text.contrast,
                              flex: 1,
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.itemName} {selectedItemQuantities[item.id] ? `(${selectedItemQuantities[item.id]})` : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: theme.radii.small,
                                padding: '2px 6px'
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateItemQuantity(item.id, -1);
                                }}
                                sx={{
                                  color: theme.colors.text.contrast,
                                  padding: '0px',
                                  width: 18,
                                  height: 18,
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                              >
                                <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>-</Typography>
                              </IconButton>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.colors.text.contrast,
                                  fontWeight: 'bold',
                                  minWidth: 16,
                                  textAlign: 'center',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {selectedItemQuantities[item.id] || 1}
                              </Typography>
                              <IconButton
                                size="small"
                                disabled={typeof (item as any).quantity === 'number' && (selectedItemQuantities[item.id] || 1) >= (item as any).quantity}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateItemQuantity(item.id, 1);
                                }}
                                sx={{
                                  color: theme.colors.text.contrast,
                                  padding: '0px',
                                  width: 18,
                                  height: 18,
                                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                              >
                                <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>+</Typography>
                              </IconButton>
                            </Box>

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveItem(item.id);
                              }}
                              sx={{
                                color: theme.colors.text.contrast,
                                padding: '4px',
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                flexShrink: 0,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                }
                              }}
                            >
                              <CloseIcon sx={{ fontSize: '0.875rem' }} />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      {selectedItems.length === 0 && (
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', fontStyle: 'italic' }}>
                          Nenhum item adicionado
                        </Typography>
                      )}
                    </ScrollIndicatorBox>
                    {/* Selected shared items chips */}
                    {selectedSharedItems && selectedSharedItems.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedSharedItems.map((si) => (
                          <Chip
                            key={`${si.ownerId}-${si.itemId}`}
                            label={`${si.itemName} x${si.quantity} (De: ${si.ownerName})`}
                            size="small"
                            onDelete={() => handleRemoveSharedItem(si.itemId, si.ownerId)}
                            sx={{
                              backgroundColor: theme.colors.primary,
                              color: '#fff',
                              fontSize: '0.7rem',
                              height: 24,
                              '& .MuiChip-deleteIcon': {
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '0.875rem',
                                '&:hover': { color: '#fff' },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <AddButton onClick={() => setShowItemSelector(true)}>
                    <AddCircleOutlineIcon sx={{ color: theme.colors.primary }} />
                  </AddButton>
                </SectionPaper>
              </Grid>

              {/* Fotos do Evento */}
              <Grid size={{ xs: 12 }}>
                <SectionPaper sx={{ minHeight: '120px', backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" sx={{ color: theme.colors.text.primary, mb: 1 }}>Fotos do Evento:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                    {photos.map((photo, index) => (
                      <Box key={index} sx={{ position: 'relative', flexShrink: 0 }}>
                        <Box
                          component="img"
                          src={photo}
                          sx={{
                            width: 100,
                            height: 100,
                            borderRadius: theme.radii.small,
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePhoto(photo)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                            padding: '4px',
                          }}
                        >
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      component="label"
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: theme.radii.small,
                        border: `1px dashed ${theme.colors.primary}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.primary,
                        flexShrink: 0,
                      }}
                    >
                      {uploadingPhoto ? <CircularProgress size={24} /> : <PhotoCamera />}
                      <Typography variant="caption" sx={{ mt: 1 }}>Adicionar</Typography>
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                      />
                    </Button>
                  </Box>
                </SectionPaper>
              </Grid>

              {/* Tarefas */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionPaper sx={{ backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" gutterBottom sx={{ color: theme.colors.text.primary }}>TAREFAS</Typography>
                  <Box>
                    {tasks.map((task) => (
                      <Box key={task.id} sx={{ mb: 1, pb: 0.5, borderBottom: `1px solid ${theme.colors.border}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            checked={task.checked}
                            onChange={() => handleToggleTask(task.id)}
                            sx={{
                              color: theme.colors.primary,
                              '&.Mui-checked': { color: theme.colors.primary }
                            }}
                          />
                          <TextField
                            value={task.label}
                            onChange={(e) => handleUpdateTaskLabel(task.id, e.target.value)}
                            placeholder="Nova tarefa"
                            variant="standard"
                            size="small"
                            fullWidth
                            sx={{ ml: 1, '& .MuiInput-root': { color: theme.colors.text.primary } }}
                          />
                          <IconButton size="small" onClick={() => handleDeleteTask(task.id)}>
                            <DeleteIcon sx={{ fontSize: '1rem', color: theme.colors.text.secondary }} />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5.5, pb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Data:</Typography>
                          <TextField
                            type="date"
                            value={task.date || ''}
                            onChange={(e) => handleUpdateTaskDate(task.id, e.target.value)}
                            variant="standard"
                            size="small"
                            inputProps={{ 'aria-label': `Data da tarefa ${task.label || ''}`.trim() }}
                            sx={{ width: 150, '& input': { color: task.date ? theme.colors.text.primary : theme.colors.text.secondary, fontSize: '0.8rem' } }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <AddButton onClick={handleAddTask}>
                    <AddCircleOutlineIcon sx={{ color: theme.colors.primary }} />
                  </AddButton>
                </SectionPaper>
              </Grid>

              {/* Ações - Apenas quando evento já existe */}
              {eventData?.id && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box height="100%" display="flex" flexDirection="column" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      onClick={handleSendWhatsApp}
                      disabled={selectedGuests.filter(g => g.phone).length === 0}
                      sx={{
                        backgroundColor: theme.colors.secondary,
                        color: theme.colors.text.onSecondary,
                        borderRadius: theme.radii.medium,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        padding: '12px 24px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: theme.colors.secondary,
                          opacity: 0.8,
                        },
                        '&:disabled': {
                          backgroundColor: theme.colors.secondary,
                          opacity: 0.5,
                        }
                      }}
                      fullWidth
                    >
                      Enviar via WhatsApp
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<MailOutlineIcon />}
                      onClick={handleSendEmail}
                      disabled={selectedGuests.filter(g => g.email).length === 0}
                      sx={{
                        backgroundColor: theme.colors.secondary,
                        color: theme.colors.text.onSecondary,
                        borderRadius: theme.radii.medium,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        padding: '12px 24px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: theme.colors.secondary,
                          opacity: 0.8,
                        },
                        '&:disabled': {
                          backgroundColor: theme.colors.secondary,
                          opacity: 0.5,
                        }
                      }}
                      fullWidth
                    >
                      Enviar por Email
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CalendarTodayIcon />}
                      onClick={handleAddToGoogleCalendar}
                      sx={{
                        backgroundColor: theme.colors.secondary,
                        color: theme.colors.text.onSecondary,
                        borderRadius: theme.radii.medium,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        padding: '12px 24px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: theme.colors.secondary,
                          opacity: 0.8,
                        },
                      }}
                      fullWidth
                    >
                      Adicionar ao Calendário
                    </Button>
                  </Box>
                </Grid>
              )}

              {/* Observações */}
              <Grid size={{ xs: 12 }}>
                <SectionPaper sx={{ backgroundColor: theme.colors.card }}>
                  <Typography variant="h6" gutterBottom sx={{ color: theme.colors.text.primary }}>
                    OBSERVAÇÕES:
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    variant="outlined"
                    placeholder="Adicione observações sobre o evento..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: theme.colors.text.primary,
                        '& fieldset': {
                          borderColor: theme.colors.border,
                        },
                      },
                    }}
                  />
                </SectionPaper>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
          <Button
            fullWidth
            onClick={handleOpenCollaborators}
            sx={{
              backgroundColor: theme.colors.secondary,
              color: theme.colors.text.onSecondary,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: theme.radii.medium,
              py: 1.25,
              '&:hover': { backgroundColor: theme.colors.secondary, opacity: 0.9 },
            }}
          >
            Convide alguém para colaborar
          </Button>
          {pendingCollaborators.length > 0 && (
            <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mt: 0.5, textAlign: 'center' }}>
              {pendingCollaborators.length} colaborador(es) — o convite será enviado ao salvar o evento.
            </Typography>
          )}
        </Box>

        <Dialog open={showCollaboratorDialog} onClose={() => setShowCollaboratorDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Convidar colaborador</DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              Informe o email da conta Hostly de quem vai co-editar o evento. O convite será enviado ao salvar o evento.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Email do colaborador"
                type="email"
                value={collaboratorEmail}
                onChange={(e) => setCollaboratorEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCollaborator();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddCollaborator}
                disabled={!collaboratorEmail.trim()}
                sx={{ backgroundColor: theme.colors.primary, textTransform: 'none', flexShrink: 0 }}
              >
                Adicionar
              </Button>
            </Box>
            {pendingCollaborators.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {pendingCollaborators.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => {
                      setPendingCollaborators((prev) => prev.filter((x) => x !== email));
                      setHasChanges(true);
                      collaboratorsDirtyRef.current = true;
                    }}
                    sx={{ backgroundColor: `${theme.colors.primary}18`, color: theme.colors.text.primary }}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShowCollaboratorDialog(false)} sx={{ color: theme.colors.text.primary, textTransform: 'none' }}>
              Concluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Footer - Static no fundo do Flex Container */}
        <Box
          sx={{
            backgroundColor: theme.colors.background,
            borderTop: `1px solid ${theme.colors.border}`,
            paddingBottom: `${safeArea.bottom}px`,
            paddingLeft: `${safeArea.left}px`,
            paddingRight: `${safeArea.right}px`,
            flexShrink: 0, // Não encolher
            zIndex: 1,
          }}
        >
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleCloseDialog}
              disabled={saving}
              sx={{
                color: theme.colors.text.secondary,
                textTransform: 'none',
              }}
            >
              Cancelar
            </Button>
            {isEditingDraft && (
              <Button
                onClick={handlePublishEvent}
                disabled={saving}
                variant="contained"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#d97706',
                  },
                  '&:disabled': {
                    backgroundColor: '#f59e0b',
                    opacity: 0.5,
                  },
                }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Publicar Evento'}
              </Button>
            )}
            <Button
              onClick={handleSaveEvent}
              disabled={saving}
              variant="contained"
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
                '&:disabled': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.5,
                },
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar Evento'}
            </Button>
          </Box>
        </Box>
      </Box >
      {/* Fim do Container Principal Flex */}
      {/* Dialog de seleção de convidados */}
      <Dialog open={showGuestSelector} onClose={() => setShowGuestSelector(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: theme.colors.text.primary }}>
              Selecionar Convidados
            </Typography>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                setShowGuestSelector(false);
                setShowNewGuestDialog(true);
              }}
              sx={{
                color: theme.colors.primary,
                textTransform: 'none',
              }}
            >
              Novo
            </Button>
          </Box>
          {loadingGuests ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {availableGuests.map((guest) => {
                const isSelected = selectedGuests.find(g => g.id === guest.id);
                return (
                  <ListItemButton
                    key={guest.id}
                    onClick={() => handleAddGuest(guest)}
                    sx={{
                      backgroundColor: isSelected ? `${theme.colors.primary}20` : 'transparent',
                      borderRadius: theme.radii.small,
                      mb: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={guest.lastName ? `${guest.name} ${guest.lastName}` : guest.name}
                      secondary={guest.email || guest.phone}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: theme.colors.text.primary
                        },
                        '& .MuiListItemText-secondary': {
                          color: theme.colors.text.secondary,
                          fontSize: '0.75rem',
                        }
                      }}
                    />
                    {isSelected ? (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveGuest(guest.id);
                        }}
                        sx={{
                          color: '#f44336',
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : (
                      <Checkbox
                        checked={false}
                        sx={{
                          color: theme.colors.text.secondary,
                        }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
              {availableGuests.length === 0 && (
                <Typography sx={{ p: 2, textAlign: 'center', color: theme.colors.text.secondary }}>
                  Nenhum convidado cadastrado
                </Typography>
              )}
            </List>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setShowGuestSelector(false)}
              variant="contained"
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              Concluir ({selectedGuests.length})
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog de criar novo convidado */}
      <Dialog open={showNewGuestDialog} onClose={() => !savingNewGuest && setShowNewGuestDialog(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.colors.text.primary }}>
            Novo Convidado
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nome *"
              value={newGuestName}
              onChange={(e) => setNewGuestName(e.target.value)}
              sx={{
                '& .MuiInputLabel-root': {
                  backgroundColor: theme.colors.background,
                  px: 0.5,
                },
              }}
            />
            <TextField
              fullWidth
              label="Sobrenome"
              value={newGuestLastName}
              onChange={(e) => setNewGuestLastName(e.target.value)}
              sx={{
                '& .MuiInputLabel-root': {
                  backgroundColor: theme.colors.background,
                  px: 0.5,
                },
              }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newGuestEmail}
              onChange={(e) => setNewGuestEmail(e.target.value)}
              sx={{
                '& .MuiInputLabel-root': {
                  backgroundColor: theme.colors.background,
                  px: 0.5,
                },
              }}
            />
            <TextField
              fullWidth
              label="Telefone"
              value={newGuestPhone}
              onChange={(e) => setNewGuestPhone(e.target.value)}
              sx={{
                '& .MuiInputLabel-root': {
                  backgroundColor: theme.colors.background,
                  px: 0.5,
                },
              }}
            />
          </Box>
          <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setShowNewGuestDialog(false);
                setShowGuestSelector(true);
              }}
              disabled={savingNewGuest}
              sx={{ color: theme.colors.text.secondary }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateNewGuest}
              disabled={savingNewGuest || !newGuestName.trim()}
              variant="contained"
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              {savingNewGuest ? <CircularProgress size={20} color="inherit" /> : 'Criar'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog de envio via WhatsApp */}
      <Dialog open={showWhatsAppDialog} onClose={() => setShowWhatsAppDialog(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.colors.text.primary }}>
            Enviar Convite via WhatsApp
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: theme.colors.text.secondary }}>
            Selecione um convidado para enviar o convite:
          </Typography>
          <List>
            {selectedGuests.filter(g => g.phone).map((guest) => (
              <ListItemButton
                key={guest.id}
                onClick={() => {
                  handleSendToGuest(guest, 'whatsapp');
                  setShowWhatsAppDialog(false);
                }}
                sx={{
                  borderRadius: theme.radii.small,
                  mb: 1,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <ListItemText
                  primary={guest.lastName ? `${guest.name} ${guest.lastName}` : guest.name}
                  secondary={guest.phone}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: theme.colors.text.primary,
                    },
                    '& .MuiListItemText-secondary': {
                      color: theme.colors.text.secondary,
                    },
                  }}
                />
                <WhatsAppIcon sx={{ color: '#25D366' }} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Dialog de envio via Email */}
      <Dialog open={showEmailDialog} onClose={() => setShowEmailDialog(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.colors.text.primary }}>
            Enviar Convite por Email
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: theme.colors.text.secondary }}>
            Selecione um convidado para enviar o convite:
          </Typography>
          <List>
            {selectedGuests.filter(g => g.email).map((guest) => (
              <ListItemButton
                key={guest.id}
                onClick={() => {
                  handleSendToGuest(guest, 'email');
                  setShowEmailDialog(false);
                }}
                sx={{
                  borderRadius: theme.radii.small,
                  mb: 1,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <ListItemText
                  primary={guest.lastName ? `${guest.name} ${guest.lastName}` : guest.name}
                  secondary={guest.email}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: theme.colors.text.primary,
                    },
                    '& .MuiListItemText-secondary': {
                      color: theme.colors.text.secondary,
                    },
                  }}
                />
                <MailOutlineIcon sx={{ color: theme.colors.primary }} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Dialog de seleção de receitas */}
      <Dialog open={!!imagePreview} onClose={() => setImagePreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{imagePreview?.name}</DialogTitle>
        <DialogContent>
          {imagePreview && (
            <Box
              component="img"
              src={imagePreview.src}
              alt={imagePreview.name}
              sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreview(null)} sx={{ color: theme.colors.text.secondary }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showRecipeSelector} onClose={() => setShowRecipeSelector(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={scrollableSx(theme)}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.colors.text.primary }}>
            Selecionar Receitas
          </Typography>
          {loadingRecipes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {availableRecipes.map((recipe) => {
                const isSelected = selectedRecipes.find(r => r.id === recipe.id);
                return (
                  <ListItemButton
                    key={recipe.id}
                    onClick={() => {
                      if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                      handleAddRecipe(recipe);
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onTouchStart={() => startLongPress(recipe.imageUrl, recipe.title)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    onMouseDown={() => startLongPress(recipe.imageUrl, recipe.title)}
                    onMouseUp={cancelLongPress}
                    onMouseLeave={cancelLongPress}
                    sx={{
                      backgroundColor: isSelected ? `${theme.colors.primary}10` : 'transparent',
                      borderRadius: theme.radii.small,
                      mb: 1,
                      border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                      '&:hover': {
                        backgroundColor: isSelected ? `${theme.colors.primary}20` : `${theme.colors.border}`,
                      },
                    }}
                  >
                    {recipe.imageUrl ? (
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          borderRadius: theme.radii.small,
                          backgroundImage: `url(${recipe.imageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 100,
                          height: 100,
                          backgroundColor: theme.colors.secondary,
                          mr: 2,
                          fontSize: '2.5rem',
                          flexShrink: 0,
                        }}
                      >
                        R
                      </Avatar>
                    )}
                    <ListItemText
                      primary={recipe.title}
                      secondary={
                        <Box>
                          {recipe.ingredients && recipe.ingredients.length > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', color: theme.colors.text.secondary }}>
                              {recipe.ingredients.length} ingrediente{recipe.ingredients.length !== 1 ? 's' : ''}
                            </Typography>
                          )}
                          {recipe.prepTime && (
                            <Typography variant="caption" sx={{ display: 'block', color: theme.colors.text.secondary }}>
                              {recipe.prepTime} min
                            </Typography>
                          )}
                          {recipe.servings && (
                            <Typography variant="caption" sx={{ display: 'block', color: theme.colors.text.secondary }}>
                              {recipe.servings} porções
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: theme.colors.text.primary,
                          fontWeight: isSelected ? 'bold' : 'normal',
                          fontSize: '1rem',
                        }
                      }}
                    />
                    {isSelected && (
                      <Checkbox
                        checked
                        sx={{
                          color: theme.colors.primary,
                          '&.Mui-checked': {
                            color: theme.colors.primary,
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
              {availableRecipes.length === 0 && (
                <Typography sx={{ p: 2, textAlign: 'center', color: theme.colors.text.secondary }}>
                  Nenhuma receita cadastrada
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border}`, p: 1.5 }}>
          <Button
            fullWidth
            onClick={() => setShowRecipeSelector(false)}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.8 },
            }}
          >
            Concluir ({selectedRecipes.length})
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog de seleção de itens */}
      <Dialog open={showItemSelector} onClose={() => { setShowItemSelector(false); setInventoryTab('mine'); }} maxWidth="xs" fullWidth>
        <DialogContent sx={scrollableSx(theme)}>
          <Typography variant="h6" sx={{ mb: 1, color: theme.colors.text.primary }}>
            Selecionar Itens do Acervo
          </Typography>
          {/* Tabs: Meu Acervo / Compartilhados */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant={inventoryTab === 'mine' ? 'contained' : 'outlined'}
              onClick={() => setInventoryTab('mine')}
              sx={{
                flex: 1,
                textTransform: 'none',
                fontSize: '0.8rem',
                borderRadius: theme.radii.small,
                ...(inventoryTab === 'mine'
                  ? { backgroundColor: theme.colors.primary, color: theme.colors.text.contrast, '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 } }
                  : { borderColor: theme.colors.primary, color: theme.colors.primary }),
              }}
            >
              Meu Acervo
            </Button>
            <Button
              size="small"
              variant={inventoryTab === 'shared' ? 'contained' : 'outlined'}
              onClick={() => {
                setInventoryTab('shared');
                if (sharedItems.length === 0 && !loadingSharedItems) {
                  loadSharedInventories();
                }
              }}
              sx={{
                flex: 1,
                textTransform: 'none',
                fontSize: '0.8rem',
                borderRadius: theme.radii.small,
                ...(inventoryTab === 'shared'
                  ? { backgroundColor: '#7B68EE', color: '#fff', '&:hover': { backgroundColor: '#6A5ACD' } }
                  : { borderColor: '#7B68EE', color: '#7B68EE' }),
              }}
            >
              Compartilhados
            </Button>
          </Box>

          {/* Meu Acervo tab */}
          {inventoryTab === 'mine' && (
            <List>
              {availableItems.map((item) => {
                const itemSelected = selectedItems.some(i => i.id === item.id);
                return (
                <ListItemButton
                  key={item.id}
                  onClick={() => {
                    if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                    handleAddItem(item);
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  onTouchStart={() => startLongPress(item.photoUrl, item.itemName)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onMouseDown={() => startLongPress(item.photoUrl, item.itemName)}
                  onMouseUp={cancelLongPress}
                  onMouseLeave={cancelLongPress}
                  sx={{
                    borderRadius: theme.radii.small,
                    mb: 0.5,
                    border: `1px solid ${itemSelected ? theme.colors.primary : theme.colors.border}`,
                    backgroundColor: itemSelected ? `${theme.colors.primary}10` : 'transparent',
                  }}
                >
                  <Checkbox size="small" checked={itemSelected} sx={{ p: 0.5, mr: 0.5 }} />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: theme.radii.small,
                      backgroundImage: item.photoUrl
                        ? `url(${item.photoUrl})`
                        : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 2,
                      flexShrink: 0,
                    }}
                  >
                    {!item.photoUrl && <Typography sx={{ fontSize: '1.5rem' }}>📦</Typography>}
                  </Box>
                  <ListItemText
                    primary={item.itemName}
                    secondary={item.category}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: theme.colors.text.primary,
                        fontWeight: 500,
                      },
                      '& .MuiListItemText-secondary': {
                        color: theme.colors.text.secondary,
                        fontSize: '0.75rem',
                      },
                    }}
                  />
                </ListItemButton>
                );
              })}
              {availableItems.length === 0 && (
                <Typography sx={{ p: 2, textAlign: 'center', color: theme.colors.text.secondary }}>
                  Nenhum item disponível no acervo
                </Typography>
              )}
            </List>
          )}

          {/* Compartilhados tab */}
          {inventoryTab === 'shared' && (
            <Box>
              {loadingSharedItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={28} sx={{ color: '#7B68EE' }} />
                </Box>
              ) : sharedInventories.length === 0 ? (
                <Typography sx={{ p: 2, textAlign: 'center', color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                  Nenhum acervo compartilhado com você
                </Typography>
              ) : (
                sharedInventories.map((inv) => {
                  const ownerItems = sharedItems.filter(si => si._ownerId === inv.ownerId);
                  return (
                    <Box key={inv.ownerId} sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#7B68EE',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          display: 'block',
                          mb: 0.5,
                          px: 1,
                        }}
                      >
                        De: {inv.ownerName}
                      </Typography>
                      <List dense disablePadding>
                        {ownerItems.map((item) => {
                          const alreadyAdded = selectedSharedItems?.find(
                            si => si.itemId === item.id && si.ownerId === item._ownerId
                          );
                          return (
                            <ListItemButton
                              key={item.id}
                              onClick={() => !alreadyAdded && setPendingSharedItem({ item, qty: 0 })}
                              disabled={!!alreadyAdded}
                              sx={{ opacity: alreadyAdded ? 0.5 : 1, py: 0.5 }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: theme.radii.small,
                                  backgroundImage: item.photoUrl
                                    ? `url(${item.photoUrl})`
                                    : 'linear-gradient(135deg, #C4B5E1 0%, #9B8EC4 100%)',
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: 2,
                                  flexShrink: 0,
                                }}
                              >
                                {!item.photoUrl && <Typography sx={{ fontSize: '1.2rem' }}>📦</Typography>}
                              </Box>
                              <ListItemText
                                primary={item.itemName}
                                secondary={alreadyAdded ? 'Já adicionado' : (item.category || '')}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: theme.colors.text.primary,
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                  },
                                  '& .MuiListItemText-secondary': {
                                    color: alreadyAdded ? '#7B68EE' : theme.colors.text.secondary,
                                    fontSize: '0.7rem',
                                  },
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                        {ownerItems.length === 0 && (
                          <Typography sx={{ p: 1, pl: 2, color: theme.colors.text.secondary, fontSize: '0.75rem', fontStyle: 'italic' }}>
                            Nenhum item neste acervo
                          </Typography>
                        )}
                      </List>
                    </Box>
                  );
                })
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border}`, p: 1.5 }}>
          <Button
            fullWidth
            onClick={() => { setShowItemSelector(false); setInventoryTab('mine'); }}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.8 },
            }}
          >
            Concluir ({selectedItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de seleção de quantidade do acervo compartilhado */}
      <Dialog
        open={!!pendingSharedItem}
        onClose={() => setPendingSharedItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 1, color: theme.colors.text.primary }}>
            {pendingSharedItem?.item.itemName}
          </Typography>
          {pendingSharedItem && maxQuantityForSharedItem(pendingSharedItem.item) !== Infinity && (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
              Disponível: {pendingSharedItem.item.quantity}
            </Typography>
          )}
          <TextField
            label="Quantidade"
            fullWidth
            placeholder="1"
            value={pendingSharedItem?.qty ? String(pendingSharedItem.qty) : ''}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, '');
              setPendingSharedItem(prev => {
                if (!prev) return null;
                if (raw === '') return { ...prev, qty: 0 };
                const max = maxQuantityForSharedItem(prev.item);
                return { ...prev, qty: Math.min(parseInt(raw, 10), max) };
              });
            }}
            slotProps={{
              input: { inputMode: 'numeric' },
              htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
            }}
          />
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={() => setPendingSharedItem(null)} sx={{ color: theme.colors.text.secondary, textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => pendingSharedItem && handleAddSharedItem(pendingSharedItem.item, Math.max(1, pendingSharedItem.qty))}
            sx={{ backgroundColor: theme.colors.primary, color: '#fff', textTransform: 'none' }}
          >
            Adicionar
          </Button>
        </Box>
      </Dialog>

      {/* Dialog de confirmação ao sair */}
      <Dialog
        open={showExitDialog}
        onClose={() => setShowExitDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.colors.text.primary }}>
            Salvar rascunho?
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
            Você tem alterações não salvas. Deseja salvar como rascunho antes de sair?
          </Typography>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => {
              setShowExitDialog(false);
              onClose();
            }}
            sx={{ color: theme.colors.text.secondary, textTransform: 'none' }}
          >
            Descartar
          </Button>
          <Button
            onClick={handleSaveDraft}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.8,
              },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : (eventData?.id ? 'Salvar' : 'Salvar Rascunho')}
          </Button>
        </Box>
      </Dialog>

      {/* Dialog de aviso de alergia */}
      <Dialog
        open={showAllergyWarning}
        onClose={() => setShowAllergyWarning(false)}
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#d32f2f',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              ⚠️ Atenção: Possíveis Alergias
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{ color: theme.colors.text.primary, mb: 2 }}
          >
            Foram detectados possíveis conflitos entre ingredientes das receitas e alergias dos convidados:
          </Typography>
          <Box sx={{ mb: 2 }}>
            {allergyConflicts.map((conflict, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: theme.colors.text.secondary,
                  backgroundColor: '#fff3e0',
                  p: 1,
                  borderRadius: 1,
                  mb: 1,
                  border: '1px solid #ffcc02',
                }}
              >
                <strong>{conflict.guestName}</strong> tem alergia a <strong>{conflict.allergens.join(', ')}</strong>, presente na receita "<strong>{conflict.recipeTitle}</strong>"
              </Typography>
            ))}
          </Box>
          <Typography
            variant="body2"
            sx={{ color: theme.colors.text.secondary }}
          >
            Considere remover as receitas conflitantes ou entrar em contato com os convidados para confirmar suas restrições.
          </Typography>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setShowAllergyWarning(false)}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.8,
              },
            }}
          >
            Entendi
          </Button>
        </Box>
      </Dialog>

      {/* Dialog de preview do item */}
      <Dialog
        open={itemPreviewDialog.open}
        onClose={() => setItemPreviewDialog({ open: false, item: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {itemPreviewDialog.item && (
            <Box>
              {/* Imagem do item */}
              <Box
                sx={{
                  width: '100%',
                  height: 250,
                  backgroundImage: itemPreviewDialog.item.photoUrl
                    ? `url(${itemPreviewDialog.item.photoUrl})`
                    : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {!itemPreviewDialog.item.photoUrl && (
                  <Typography sx={{ fontSize: '4rem', opacity: 0.5 }}>📦</Typography>
                )}
                <IconButton
                  onClick={() => setItemPreviewDialog({ open: false, item: null })}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Nome e detalhes do item */}
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: theme.colors.text.primary,
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  {itemPreviewDialog.item.itemName}
                </Typography>

                {itemPreviewDialog.item.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.colors.text.secondary,
                      mb: 2,
                    }}
                  >
                    {itemPreviewDialog.item.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {itemPreviewDialog.item.category && (
                    <Chip
                      label={itemPreviewDialog.item.category}
                      size="small"
                      sx={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.text.contrast,
                      }}
                    />
                  )}
                  {itemPreviewDialog.item.brand && (
                    <Chip
                      label={itemPreviewDialog.item.brand}
                      size="small"
                      sx={{
                        backgroundColor: theme.colors.secondary,
                        color: theme.colors.text.primary,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de seleção de data customizado */}
      <Dialog
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: theme.colors.text.primary, textAlign: 'center' }}>
            Selecionar Data
          </Typography>

          {(() => {
            const currentDate = date ? new Date(date + 'T00:00:00') : new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth();

            const [selectedYear, setSelectedYear] = React.useState(currentYear);
            const [selectedMonth, setSelectedMonth] = React.useState(currentMonth);
            const [selectedDay, setSelectedDay] = React.useState(currentDate.getDate());

            const months = [
              'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
              'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
            ];

            const getDaysInMonth = (year: number, month: number) => {
              return new Date(year, month + 1, 0).getDate();
            };

            const getFirstDayOfMonth = (year: number, month: number) => {
              return new Date(year, month, 1).getDay();
            };

            const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
            const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
            const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
            const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

            const handleConfirm = () => {
              const newDate = new Date(selectedYear, selectedMonth, selectedDay);
              const formattedDate = newDate.toISOString().split('T')[0];
              setDate(formattedDate);
              setShowDatePicker(false);
            };

            return (
              <Box>
                {/* Seletor de Mês e Ano */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Mês"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    SelectProps={{
                      native: true,
                    }}
                    sx={{
                      '& .MuiInputLabel-root': {
                        backgroundColor: theme.colors.background,
                        px: 0.5,
                      },
                    }}
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label="Ano"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    SelectProps={{
                      native: true,
                    }}
                    sx={{
                      '& .MuiInputLabel-root': {
                        backgroundColor: theme.colors.background,
                        px: 0.5,
                      },
                    }}
                  >
                    {Array.from({ length: 10 }, (_, i) => currentYear - 2 + i).map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </TextField>
                </Box>

                {/* Grade de dias */}
                <Box sx={{ mb: 3 }}>
                  {/* Cabeçalho dos dias da semana */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                    {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                      <Box
                        key={i}
                        sx={{
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: theme.colors.text.secondary,
                          fontSize: '0.875rem',
                        }}
                      >
                        {day}
                      </Box>
                    ))}
                  </Box>

                  {/* Grade de dias */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                    {emptyDays.map((_, i) => (
                      <Box key={`empty-${i}`} />
                    ))}
                    {days.map((day) => (
                      <Button
                        key={day}
                        onClick={() => setSelectedDay(day)}
                        sx={{
                          minWidth: 40,
                          height: 40,
                          p: 0,
                          backgroundColor: selectedDay === day ? theme.colors.primary : 'transparent',
                          color: selectedDay === day ? theme.colors.text.contrast : theme.colors.text.primary,
                          borderRadius: theme.radii.small,
                          '&:hover': {
                            backgroundColor: selectedDay === day ? theme.colors.primary : theme.colors.card,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {day}
                      </Button>
                    ))}
                  </Box>
                </Box>

                {/* Botões de ação */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => setShowDatePicker(false)}
                    sx={{ color: theme.colors.text.secondary, textTransform: 'none' }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                      backgroundColor: theme.colors.primary,
                      color: theme.colors.text.contrast,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: theme.colors.primary,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Confirmar
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Dialog de Alerta */}
      <Dialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ open: false, message: '' })}
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ color: theme.colors.text.primary, textAlign: 'center' }}>
            {alertDialog.message}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              onClick={() => setAlertDialog({ open: false, message: '' })}
              variant="contained"
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.9,
                },
              }}
            >
              OK
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog >
  );
};
